import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, ModalContent, ModalFooter, ModalFooterButtons, ModalHeader } from 'ui/molecules/modal';
import Hint from 'ui/atoms/hint';
import Translate from 'ui/atoms/translate';
import Select from 'ui/atoms/select';
import LoadingRing from 'ui/atoms/loading-ring';
import { PortfolioApi } from 'api/apis';
import { NetworkTypeEnum, Wallet, WalletTypeEnum } from 'api/models';
import useApiCall from 'hooks/use-api-call';

export interface AddWalletModalProps {
  productId: string;
  productNetwork?: NetworkTypeEnum;
  onClose: () => void;
  onSuccess: () => void;
}

const WALLET_TYPE_LABELS: Record<string, string> = {
  [WalletTypeEnum.CASHLINK]: 'Cashlink',
  [WalletTypeEnum.MOBILE]: 'Mobile',
  [WalletTypeEnum.TANGANY]: 'Tangany',
  [WalletTypeEnum.GENERIC]: 'Generic',
};

function walletLabel(w: Wallet, network?: NetworkTypeEnum): string {
  const typeLabel = WALLET_TYPE_LABELS[w.type] || w.type;
  if (network === NetworkTypeEnum.ETHEREUM && w.ethInfo) return `${typeLabel} – ${w.ethInfo.address.slice(0, 10)}…`;
  if (network === NetworkTypeEnum.STELLAR && w.stellarInfo)
    return `${typeLabel} – ${w.stellarInfo.accountId.slice(0, 10)}…`;
  if (w.stellarInfo) return `${typeLabel} – ${w.stellarInfo.accountId.slice(0, 10)}…`;
  if (w.ethInfo) return `${typeLabel} – ${w.ethInfo.address.slice(0, 10)}…`;
  return `${typeLabel} – ${w.id.slice(0, 8)}`;
}

const POLL_INTERVAL = 3000;

const AddWalletModal: FunctionComponent<AddWalletModalProps> = ({ productId, productNetwork, onClose, onSuccess }) => {
  const { withApi, makeAuthenticatedApi, loading } = useApiCall();

  const api = useMemo(() => makeAuthenticatedApi(PortfolioApi), [makeAuthenticatedApi]);

  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [selectedWalletId, setSelectedWalletId] = useState('');
  const [pendingTfa, setPendingTfa] = useState(false);
  const [assignmentId, setAssignmentId] = useState('');
  const [success, setSuccess] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [error, setError] = useState<'generic' | 'tfa_setup' | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    withApi(async () => {
      const res = await api.portfolioAvailableProductsWalletsList({ productId });
      setWallets(res);
    });
  }, [withApi, api, productId]);

  const startPolling = useCallback(
    (id: string) => {
      pollRef.current = setInterval(async () => {
        try {
          const status = await api.portfolioAssignmentsStatusRetrieve({ assignmentId: id });
          if (status.status === 'SUCCEEDED') {
            if (pollRef.current) clearInterval(pollRef.current);
            setPendingTfa(false);
            setSuccess(true);
          } else if (status.status === 'FAILED') {
            if (pollRef.current) clearInterval(pollRef.current);
            setPendingTfa(false);
            setError('generic');
          }
        } catch (e) {
          console.warn('add-wallet polling error', e);
        }
      }, POLL_INTERVAL);
    },
    [api],
  );

  const onSubmit = useCallback(async () => {
    setError(null);
    withApi(async () => {
      try {
        const result = await api.portfolioAddWalletCreate({
          productId,
          addWalletRequestRequest: { walletId: selectedWalletId },
        });
        setAssignmentId(result.id);
        if (result.status === 'PENDING') {
          setPendingTfa(true);
          startPolling(result.id);
        } else {
          setSuccess(true);
        }
      } catch (e: any) {
        if (e?.hasErrorCode?.('tfa_setup_required')) {
          setError('tfa_setup');
        } else {
          setError('generic');
        }
      }
    });
  }, [withApi, api, productId, selectedWalletId, startPolling]);

  const onCancel = useCallback(async () => {
    if (!assignmentId) return;
    if (pollRef.current) clearInterval(pollRef.current);
    try {
      await api.portfolioAssignmentsCancelCreate({
        assignmentId,
      });
    } catch {
      // best-effort cancel
    }
    onClose();
  }, [api, assignmentId, onClose]);

  const handleClose = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (success || pendingTfa) onSuccess();
    onClose();
  }, [onClose, onSuccess, success, pendingTfa]);

  const walletOptions = wallets.map((w) => ({ value: w.id, label: walletLabel(w, productNetwork) }));

  return (
    <Modal onClose={handleClose}>
      <ModalHeader>
        <Translate name="addWalletModal.title" />
      </ModalHeader>
      <ModalContent>
        {success ? (
          <>
            {productNetwork !== NetworkTypeEnum.ETHEREUM && <Translate name="addWalletModal.successHeading" as="p" />}
            <Hint variant="success">
              <Translate name="addWalletModal.success" />
            </Hint>
          </>
        ) : confirmingCancel ? (
          <Hint variant="warning">
            <Translate name="addWalletModal.cancelConfirm" />
          </Hint>
        ) : pendingTfa ? (
          <>
            <Translate name="addWalletModal.pendingTfaHeading" as="p" />
            <Translate name="addWalletModal.pendingTfa" as="p" />
          </>
        ) : (
          <>
            {error === 'tfa_setup' && (
              <Hint variant="warning">
                <Translate name="addWalletModal.tfaSetupRequired" />
              </Hint>
            )}
            {error === 'generic' && (
              <Hint variant="danger">
                <Translate name="addWalletModal.error" />
              </Hint>
            )}
            {loading && !wallets.length ? (
              <LoadingRing />
            ) : wallets.length === 0 && !loading ? (
              <Hint variant="info">
                <Translate name="addProductModal.noWallets" />
              </Hint>
            ) : (
              <Select
                label={<Translate name="addWalletModal.selectWallet" />}
                options={walletOptions}
                value={selectedWalletId}
                onChange={(val: string) => setSelectedWalletId(val)}
              />
            )}
          </>
        )}
      </ModalContent>
      <ModalFooter>
        <ModalFooterButtons
          actionButtons={
            success
              ? [
                  {
                    name: 'close',
                    content: <Translate name="common.close" />,
                    size: 'large',
                    onClick: handleClose,
                  },
                ]
              : confirmingCancel
              ? [
                  {
                    name: 'back',
                    content: <Translate name="common.back" />,
                    size: 'large',
                    onClick: () => setConfirmingCancel(false),
                  },
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    variant: 'primary',
                    size: 'large',
                    onClick: onCancel,
                  },
                ]
              : pendingTfa
              ? [
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: onCancel,
                  },
                ]
              : error === 'tfa_setup'
              ? [
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: () => setConfirmingCancel(true),
                  },
                ]
              : [
                  {
                    name: 'cancel',
                    content: <Translate name="common.cancel" />,
                    size: 'large',
                    onClick: onClose,
                  },
                  {
                    name: 'submit',
                    content: <Translate name="common.add" />,
                    variant: 'primary',
                    size: 'large',
                    disabled: !selectedWalletId || loading,
                    onClick: onSubmit,
                  },
                ]
          }
        />
      </ModalFooter>
    </Modal>
  );
};

export default AddWalletModal;
